import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { SafeHtmlPipe } from '../../../shared/pipe/safe-html.pipe';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { ToastService } from '../../../shared/services/toast.service';
import { EmailMessageApiService } from '../services/email-message-api.service';
import {
  InboxMessageDetailResponse,
  InboxAddressResponse,
  emailChannelTypeLabel,
} from '../models/email-message.model';

/**
 * Modal de detalle de un correo RECIBIDO. Consume GET /api/emails/inbox-message/{id}.
 * Recibe el id (== sourceId del ítem RECEIVED del timeline) por @Input y carga
 * al abrir. Look & feel consistente con correo-detail-modal (mismo app-modal,
 * tokens z-index, render seguro de bodyHtml con SafeHtmlPipe).
 */
@Component({
  selector: 'app-correo-recibido-detail-modal',
  standalone: true,
  imports: [CommonModule, ModalComponent, SafeHtmlPipe],
  templateUrl: './correo-recibido-detail-modal.component.html',
})
export class CorreoRecibidoDetailModalComponent implements OnChanges {
  @Input() isOpen = false;
  /** id del email_inbox_message (== sourceId del ítem RECEIVED). */
  @Input() inboxMessageId: number | null = null;
  @Output() close = new EventEmitter<void>();

  email: InboxMessageDetailResponse | null = null;
  loading = false;
  error = '';

  constructor(
    private emailApi: EmailMessageApiService,
    private datetime: DatetimeService,
    private toast: ToastService
  ) {}

  channelTypeLabel(value: string | null | undefined): string {
    return emailChannelTypeLabel(value);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] || changes['inboxMessageId']) {
      if (this.isOpen && this.inboxMessageId != null) {
        this.cargarDetalle(this.inboxMessageId);
      } else if (!this.isOpen) {
        this.email = null;
        this.error = '';
        this.loading = false;
      }
    }
  }

  private cargarDetalle(id: number): void {
    this.loading = true;
    this.error = '';
    this.email = null;

    this.emailApi.getInboxMessageDetail(id).subscribe({
      next: (data) => {
        this.email = data;
        this.loading = false;
      },
      error: (err) => {
        const msg = err.error?.message || 'Error al cargar el correo recibido';
        this.error = msg;
        this.loading = false;
        this.toast.error({ title: 'Error', message: msg });
      },
    });
  }

  onClose(): void {
    this.close.emit();
  }

  formatDate(iso?: string | null): string {
    return this.datetime.formatDateTime(iso);
  }

  // ── Direcciones por tipo ──

  private addressesOf(kind: string): InboxAddressResponse[] {
    if (!this.email?.addresses?.length) return [];
    return this.email.addresses.filter((a) => a.kind === kind && !!a.email);
  }

  get toAddresses(): string[] {
    return this.addressesOf('TO').map((a) => a.email as string);
  }

  get ccAddresses(): string[] {
    return this.addressesOf('CC').map((a) => a.email as string);
  }
}
